// 🔄 디바이스 카탈로그 어댑터 - 점진적 전환용
// [배경] 실제 Firebase 연결 전에 상수 데이터를 Firebase 형태로 변환하는 어댑터
//        기존 코드 호환성을 유지하면서 점진적으로 Firebase로 전환

import { 
  UNIFIED_DEVICE_DATA,
  getDevicesByCategory,
  getDevicesByCategoryAndBrand 
} from '../constants/unifiedDeviceData';
import { 
  DeviceCatalogDocument,
  DeviceSearchOptions,
  generateModelId,
  generateAliases,
  DeviceType,
  DeviceBrand
} from '../types/deviceCatalog';
// 🔥 실제 Firebase 서비스 import
import { DeviceCatalogService } from './deviceCatalogService';

/**
 * 🔄 상수 데이터 → Firebase 스키마 변환 어댑터
 */
export class DeviceCatalogAdapter {
  private static modelCache = new Map<string, DeviceCatalogDocument>();
  
  // 🔄 UnifiedDevice → DeviceCatalogDocument 변환
  private static convertToFirebaseSchema(devices: any[]): DeviceCatalogDocument[] {
    const modelMap = new Map<string, DeviceCatalogDocument>();
    
    devices.forEach(device => {
      const modelId = generateModelId({
        brand: device.brand,
        deviceType: device.category,
        series: device.series,
        modelEn: device.modelEn
      });
      
      if (!modelMap.has(modelId)) {
        // 새 모델 생성
        const catalogDoc: DeviceCatalogDocument = {
          id: modelId,
          deviceType: this.mapDeviceCategory(device.category),
          brand: device.brand,
          series: device.series,
          modelEn: device.modelEn,
          modelKo: device.modelKo,
          modelNumber: device.modelNumber,
          
          // 구성 옵션 배열 생성
          storageOptions: [device.storage],
          connectivityOptions: device.connectivity ? [device.connectivity] : [],
          colorOptions: device.color ? [device.color] : [],
          
          // UI 정보
          image: device.image,
          recommended: device.recommended || false,
          subcategory: device.series,
          
          // 메타데이터
          releaseYear: device.releaseYear || 2024,
          aliases: generateAliases({
            modelEn: device.modelEn,
            modelKo: device.modelKo,
            series: device.series
          }),
          
          // 관리 정보
          isActive: true,
          priority: this.calculatePriority(device),
          
          // 감사 정보 (Mock)
          createdAt: new Date(),
          updatedAt: new Date(),
          updatedBy: 'adapter-service'
        };
        
        modelMap.set(modelId, catalogDoc);
      } else {
        // 기존 모델에 구성 옵션 추가
        const existing = modelMap.get(modelId)!;
        
        if (!existing.storageOptions.includes(device.storage)) {
          existing.storageOptions.push(device.storage);
        }
        
        if (device.connectivity && !existing.connectivityOptions?.includes(device.connectivity)) {
          existing.connectivityOptions = existing.connectivityOptions || [];
          existing.connectivityOptions.push(device.connectivity);
        }
        
        if (device.color && !existing.colorOptions?.includes(device.color)) {
          existing.colorOptions = existing.colorOptions || [];
          existing.colorOptions.push(device.color);
        }
      }
    });
    
    return Array.from(modelMap.values()).sort((a, b) => {
      // 브랜드 → 출시년도 → 우선순위 순 정렬
      if (a.brand !== b.brand) return a.brand.localeCompare(b.brand);
      if (a.releaseYear !== b.releaseYear) return (b.releaseYear || 0) - (a.releaseYear || 0);
      return (a.priority || 0) - (b.priority || 0);
    });
  }
  
  // 📱 카테고리별 모델 조회 (Firebase API와 동일한 인터페이스)
  static async getModelsByCategory(
    category: DeviceType,
    options: { useCache?: boolean } = {}
  ): Promise<DeviceCatalogDocument[]> {
    const cacheKey = `category-${category}`;
    
    // 🧪 디버깅: 어댑터 호출 확인
    console.log(`🔍 어댑터 호출 - 카테고리: ${category}`);
    
    // 캐시 확인
    if (options.useCache && this.modelCache.has(cacheKey)) {
      console.log(`✅ 캐시에서 로드: ${category}`);
      return this.modelCache.get(cacheKey)!.models || [];
    }
    
    try {
      // 상수 데이터에서 해당 카테고리 필터링
      const koreanCategory = this.mapToKoreanCategory(category);
      console.log(`🔄 한국어 카테고리: ${koreanCategory}`);
      
      const devices = getDevicesByCategory(koreanCategory);
      console.log(`📊 필터된 디바이스 개수: ${devices.length}`);
      
      // Apple 태블릿만 따로 확인
      if (category === 'tablet') {
        const appleTablets = devices.filter(d => d.brand === 'Apple');
        console.log(`🍎 Apple 태블릿 개수: ${appleTablets.length}`);
        console.log('🍎 Apple 태블릿 샘플:', appleTablets.slice(0, 3).map(d => ({ 
          modelKo: d.modelKo, 
          series: d.series, 
          storage: d.storage 
        })));
      }
      
      // Firebase 스키마로 변환
      const models = this.convertToFirebaseSchema(devices);
      console.log(`🔄 변환된 모델 개수: ${models.length}`);
      
      // Apple 태블릿 변환 확인
      if (category === 'tablet') {
        const appleModels = models.filter(m => m.brand === 'Apple');
        console.log(`🍎 변환된 Apple 태블릿 개수: ${appleModels.length}`);
        console.log('🍎 변환된 Apple 태블릿 샘플:', appleModels.slice(0, 3).map(m => ({ 
          modelKo: m.modelKo, 
          series: m.series, 
          storageOptions: m.storageOptions 
        })));
      }
      
      // 캐시 저장
      if (options.useCache) {
        this.modelCache.set(cacheKey, { models } as any);
      }
      
      return models;
      
    } catch (error) {
      console.error(`❌ 어댑터 카테고리 조회 실패 (${category}):`, error);
      throw new Error(`카테고리 '${category}' 모델 조회 실패`);
    }
  }
  
  // 🏷️ 브랜드+카테고리별 모델 조회
  static async getModelsByBrandAndCategory(
    brand: DeviceBrand,
    category: DeviceType,
    options: { useCache?: boolean } = {}
  ): Promise<DeviceCatalogDocument[]> {
    const cacheKey = `brand-category-${brand}-${category}`;
    
    if (options.useCache && this.modelCache.has(cacheKey)) {
      return this.modelCache.get(cacheKey)!.models || [];
    }
    
    try {
      const koreanCategory = this.mapToKoreanCategory(category);
      const devices = getDevicesByCategoryAndBrand(koreanCategory, brand);
      
      const models = this.convertToFirebaseSchema(devices);
      
      if (options.useCache) {
        this.modelCache.set(cacheKey, { models } as any);
      }
      
      return models;
      
    } catch (error) {
      console.error(`❌ 어댑터 브랜드+카테고리 조회 실패 (${brand}, ${category}):`, error);
      throw new Error(`${brand} ${category} 모델 조회 실패`);
    }
  }
  
  // 🔍 시리즈별 모델 조회
  static async getModelsBySeries(series: string): Promise<DeviceCatalogDocument[]> {
    try {
      const allDevices = UNIFIED_DEVICE_DATA.filter(d => d.series === series);
      return this.convertToFirebaseSchema(allDevices);
    } catch (error) {
      console.error(`❌ 어댑터 시리즈 조회 실패 (${series}):`, error);
      throw new Error(`시리즈 '${series}' 모델 조회 실패`);
    }
  }
  
  // 🔍 고급 검색 (Firebase API와 동일한 인터페이스)
  static async searchModels(searchOptions: DeviceSearchOptions): Promise<DeviceCatalogDocument[]> {
    try {
      let filteredDevices = [...UNIFIED_DEVICE_DATA];
      
      // 필터 적용
      if (searchOptions.deviceType) {
        const koreanCategory = this.mapToKoreanCategory(searchOptions.deviceType);
        filteredDevices = filteredDevices.filter(d => d.category === koreanCategory);
      }
      
      if (searchOptions.brand) {
        filteredDevices = filteredDevices.filter(d => d.brand === searchOptions.brand);
      }
      
      if (searchOptions.series) {
        filteredDevices = filteredDevices.filter(d => d.series === searchOptions.series);
      }
      
      if (searchOptions.recommended !== undefined) {
        filteredDevices = filteredDevices.filter(d => 
          Boolean(d.recommended) === searchOptions.recommended
        );
      }
      
      if (searchOptions.searchQuery) {
        const query = searchOptions.searchQuery.toLowerCase();
        filteredDevices = filteredDevices.filter(d =>
          d.modelKo.toLowerCase().includes(query) ||
          d.modelEn.toLowerCase().includes(query) ||
          d.series.toLowerCase().includes(query)
        );
      }
      
      if (searchOptions.releaseYearFrom) {
        filteredDevices = filteredDevices.filter(d => 
          (d.releaseYear || 2024) >= searchOptions.releaseYearFrom!
        );
      }
      
      if (searchOptions.releaseYearTo) {
        filteredDevices = filteredDevices.filter(d => 
          (d.releaseYear || 2024) <= searchOptions.releaseYearTo!
        );
      }
      
      // 결과 변환 및 정렬
      let models = this.convertToFirebaseSchema(filteredDevices);
      
      // 정렬 적용
      if (searchOptions.orderBy) {
        models.sort((a, b) => {
          const direction = searchOptions.orderDirection === 'asc' ? 1 : -1;
          
          switch (searchOptions.orderBy) {
            case 'releaseYear':
              return ((b.releaseYear || 0) - (a.releaseYear || 0)) * direction;
            case 'priority':
              return ((a.priority || 0) - (b.priority || 0)) * direction;
            case 'modelKo':
              return a.modelKo.localeCompare(b.modelKo) * direction;
            default:
              return 0;
          }
        });
      }
      
      // 제한 적용
      if (searchOptions.limit) {
        models = models.slice(0, searchOptions.limit);
      }
      
      return models;
      
    } catch (error) {
      console.error('❌ 어댑터 검색 실패:', error);
      throw new Error('모델 검색 중 오류 발생');
    }
  }
  
  // 📊 통계 조회 (Mock Firebase API)
  static async getStatistics() {
    const models = this.convertToFirebaseSchema(UNIFIED_DEVICE_DATA);
    
    return {
      totalModels: models.length,
      byCategory: {
        smartphone: models.filter(m => m.deviceType === 'smartphone').length,
        tablet: models.filter(m => m.deviceType === 'tablet').length,
        wearable: models.filter(m => m.deviceType === 'wearable').length
      },
      byBrand: {
        Apple: models.filter(m => m.brand === 'Apple').length,
        Samsung: models.filter(m => m.brand === 'Samsung').length
      },
      recommendedCount: models.filter(m => m.recommended).length,
      lastUpdated: new Date()
    };
  }
  
  // 🔧 카테고리 매핑 유틸리티
  private static mapDeviceCategory(koreanCategory: '스마트폰' | '태블릿' | '웨어러블'): DeviceType {
    switch (koreanCategory) {
      case '스마트폰': return 'smartphone';
      case '태블릿': return 'tablet';
      case '웨어러블': return 'wearable';
      default: return 'smartphone';
    }
  }
  
  private static mapToKoreanCategory(category: DeviceType): '스마트폰' | '태블릿' | '웨어러블' {
    switch (category) {
      case 'smartphone': return '스마트폰';
      case 'tablet': return '태블릿';
      case 'wearable': return '웨어러블';
      default: return '스마트폰';
    }
  }
  
  private static calculatePriority(device: any): number {
    let priority = 1000;
    
    if (device.recommended) priority -= 500;
    if (device.releaseYear) {
      priority -= (device.releaseYear - 2020) * 10;
    }
    if (device.brand === 'Apple') priority -= 100;
    
    return Math.max(0, priority);
  }
  
  // 🔄 캐시 관리
  static clearCache(): void {
    this.modelCache.clear();
    console.log('✅ 어댑터 캐시 초기화 완료');
  }
  
  static getCacheStats(): { size: number; keys: string[] } {
    return {
      size: this.modelCache.size,
      keys: Array.from(this.modelCache.keys())
    };
  }
}

// 🎯 환경별 서비스 선택 (개발 시에는 어댑터, 프로덕션에서는 실제 Firebase)  
export const getDeviceCatalogService = () => {
  // 🔥 관리자 추가 신규 모델 표시를 위해 Firebase 사용
  const useFirebase = true; // Firebase에서 실시간 데이터 조회
  
  // 🧪 디버깅: 서비스 선택 로직 확인
  console.log('🔧 서비스 선택 로직:');
  console.log('- Firebase 실시간 데이터 사용 (신규 모델 반영)');
  console.log('- useFirebase 결정:', useFirebase);
  
  if (useFirebase) {
    // 🔥 실제 Firebase 서비스 사용
    console.log('🔥 실제 DeviceCatalogService 사용');
    return DeviceCatalogService;
  } else {
    // 어댑터 사용 (UNIFIED_DEVICE_DATA 기반 - 이미지 매핑 수정됨)
    console.log('🔄 디바이스 카탈로그 어댑터 사용 (UNIFIED_DEVICE_DATA 기반)');
    return DeviceCatalogAdapter;
  }
};
